// Meetings (reset PR3: DS artboard 1g / ui_kits/desktop/CalendarScreen.jsx). Upcoming meetings
// (FR-M9) as EventCards, each with its own auto-record switch, and Meet/Zoom/Teams notetaker join
// (FR-M3 / FR-M3a / FR-M3b / FR-M3c). Meet offers the Chrome extension (FR-M2) and the
// bot (FR-M3c) side by side — this page never offers a Teams/Zoom web-capture path, which
// is why the cards offer no "Desktop" capture choice.

import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Badge, Button, Callout, Card, EventCard, Icon, Input, Switch, type BadgeProps } from "@grackle/ui";
import { ApiFail } from "../api/client";
import {
  calendarStatus,
  joinNotetaker,
  leaveNotetaker,
  listNotetakers,
  listUpcoming,
  setMeetingAutoRecord,
  stubConnect,
  syncCalendars,
} from "../calendar/client";
import { botSupported, meetingKey, platformLabel, type MeetingPlatform } from "../calendar/platform";
import {
  botPendingMessage,
  secondsSince,
  botStatusIcon,
  botStatusLabel,
  botStatusTone,
  isActiveBotStatus,
  isRecordingBotStatus,
} from "../calendar/botStatus";
import type { CalendarEvent, CalendarStatus, NotetakerBot } from "../calendar/types";
import { calendarConnectNotice, calendarProviderLabel } from "../calendar/connectNotice";
import { AppShell } from "../components/AppShell";
import { PageLoader } from "../components/ui";
import { browserCanUseExtension } from "../live/extension";
import { useIsPlatformAdmin } from "../ops/platformAdmin";
import { useSession } from "../session";
import CalendarProviderBlock, { providerVisible } from "./CalendarProviderBlock";
import MeetingsTabs from "./MeetingsTabs";
import { TITLE_MAX } from "./RenameGrackleDialog";
import { liveNotesPath } from "./routes";
import "./library.css";
import "./meetings.css";

const PLATFORM_TONE: Record<MeetingPlatform, BadgeProps["tone"]> = {
  meet: "meet",
  zoom: "zoom",
  teams: "teams",
  other: "neutral",
};

/** F9 / SD41: an event is "live" if it's within its scheduled window right now — that's when
 * the Meetings page offers "Send bot now" instead of "Send notetaker" for one not already
 * covered by an auto-record rule or a prior manual join. An event with no `ends_at` (some
 * calendar sources omit it) is treated as live for as long as it's started. */
function isLiveNow(event: CalendarEvent, now: Date = new Date()): boolean {
  const start = new Date(event.starts_at).getTime();
  if (Number.isNaN(start) || start > now.getTime()) return false;
  if (!event.ends_at) return true;
  const end = new Date(event.ends_at).getTime();
  return Number.isNaN(end) || now.getTime() <= end;
}

/** DS: calendar times are 24-hour, mono. */
function clock24(iso: string | null): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(d);
}

/** Overline per day: "Today · Tue, Oct 7", "Tomorrow · Wed, Oct 8", or the date alone. */
function dayHeading(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(d);
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(now)) / 86_400_000);
  if (days === 0) return `Today · ${date}`;
  if (days === 1) return `Tomorrow · ${date}`;
  return date;
}

function groupByDay(events: CalendarEvent[]): { heading: string; events: CalendarEvent[] }[] {
  const groups: { heading: string; events: CalendarEvent[] }[] = [];
  for (const e of events) {
    const heading = dayHeading(e.starts_at);
    const last = groups[groups.length - 1];
    if (last && last.heading === heading) last.events.push(e);
    else groups.push({ heading, events: [e] });
  }
  return groups;
}

/** Settings' Calendars tab, where a connected calendar is managed. */
const CALENDAR_SETTINGS = "/account?tab=calendars";

export default function MeetingsPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);
  // SD50: staff get a link to the operator console when a calendar provider is unconfigured.
  const platformAdmin = useIsPlatformAdmin();

  const [status, setStatus] = useState<CalendarStatus | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [bots, setBots] = useState<NotetakerBot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState("");
  // FR-L2: optional; blank lets the server name it ("Meet meeting" etc., notetaker default_title).
  const [joinTitle, setJoinTitle] = useState("");

  const load = useCallback(async () => {
    if (!org) return;
    setError(null);
    try {
      const [st, ev, nt] = await Promise.all([
        calendarStatus(org.id),
        listUpcoming(org.id),
        listNotetakers(org.id),
      ]);
      setStatus(st);
      setEvents(ev.items);
      setBots(nt.items);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Could not load meetings.");
    } finally {
      setLoading(false);
    }
  }, [org?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // SD54: while a bot is in flight, refresh the notetaker list so its status moves on its own
  // (queued -> "not picked up after a minute" -> joining / lobby / failed with a reason).
  const hasActiveBot = bots.some((b) => isActiveBotStatus(b.ui_status));
  useEffect(() => {
    if (!org || !hasActiveBot) return;
    const id = window.setInterval(() => {
      listNotetakers(org.id)
        .then((nt) => setBots(nt.items))
        .catch(() => {});
    }, 15_000);
    return () => window.clearInterval(id);
  }, [org?.id, hasActiveBot]);

  if (sessionLoading) return <PageLoader label="Loading meetings…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;

  // FR-M9: `?connected=` / `?calendar_error=` set by the calendar OAuth redirect back here.
  const connectNotice = calendarConnectNotice(searchParams);
  const activeBots = bots.filter((b) => isActiveBotStatus(b.ui_status));
  const canUseExtension = browserCanUseExtension();
  const connectedTo = [
    status?.google.connected ? `Google Calendar${status.google.account_email ? ` · ${status.google.account_email}` : ""}` : null,
    status?.microsoft.connected
      ? `Microsoft 365${status.microsoft.account_email ? ` · ${status.microsoft.account_email}` : ""}`
      : null,
  ].filter(Boolean);
  // FR-M9: once a calendar is connected it is managed in Settings (Calendars); this page keeps
  // the connect prompt for when none is, since it is empty without one.
  const anyConnected = connectedTo.length > 0;
  const stub = status?.stub ?? false;
  const providers = (
    [
      { provider: "google", label: "Google Calendar" },
      { provider: "microsoft", label: "Microsoft 365" },
    ] as const
  ).filter((p) => providerVisible(status?.[p.provider], stub, platformAdmin));
  const syncErrors = (["google", "microsoft"] as const).filter((p) => status?.[p].connected && status[p].last_error);

  async function onStub(provider: "google" | "microsoft") {
    if (!org) return;
    setBusyId(`stub-${provider}`);
    setError(null);
    try {
      await stubConnect(org.id, provider);
      await load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Stub connect failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function onSync() {
    if (!org) return;
    setBusyId("sync");
    try {
      const ev = await syncCalendars(org.id);
      setEvents(ev.items);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Sync failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function onJoin(event: CalendarEvent) {
    if (!org) return;
    setBusyId(`join-${event.id}`);
    setError(null);
    try {
      const bot = await joinNotetaker(org.id, { event_id: event.id });
      setBots((prev) => [bot, ...prev.filter((b) => b.id !== bot.id)]);
      await load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Could not send the notetaker.");
    } finally {
      setBusyId(null);
    }
  }

  async function onJoinUrl() {
    if (!org || !joinUrl.trim()) return;
    setBusyId("join-url");
    setError(null);
    try {
      const bot = await joinNotetaker(org.id, { join_url: joinUrl.trim(), title: joinTitle.trim() || undefined });
      setBots((prev) => [bot, ...prev.filter((b) => b.id !== bot.id)]);
      setJoinUrl("");
      setJoinTitle("");
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Could not send the notetaker.");
    } finally {
      setBusyId(null);
    }
  }

  async function onLeave(botId: string) {
    setBusyId(`leave-${botId}`);
    try {
      const left = await leaveNotetaker(botId);
      setBots((prev) => prev.map((b) => (b.id === botId ? left : b)));
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Could not remove the bot.");
    } finally {
      setBusyId(null);
    }
  }

  async function onAuto(event: CalendarEvent, enabled: boolean) {
    if (!org) return;
    setBusyId(`auto-${event.id}`);
    try {
      await setMeetingAutoRecord(org.id, event.id, enabled);
      await load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Could not update auto-record.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AppShell org={org} user={user} section="meetings">
      <div className="meetings">
        <div className="meetings__head">
          <div className="meetings__head-copy">
            <h1 className="gk-h2">Meetings</h1>
            <div className="gk-sm meetings__sync">
              <Icon name={connectedTo.length > 0 ? "arrows-clockwise" : "cloud-slash"} size={16} />
              {anyConnected ? `Synced with ${connectedTo.join(" and ")}` : "No calendar connected"}
              {anyConnected ? (
                <Link to={CALENDAR_SETTINGS} className="meetings__manage">
                  Manage calendars
                </Link>
              ) : null}
            </div>
          </div>
          <Button variant="secondary" size="sm" icon="arrows-clockwise" onClick={() => void onSync()} disabled={busyId === "sync"}>
            {busyId === "sync" ? "Syncing…" : "Refresh calendars"}
          </Button>
        </div>

        <MeetingsTabs slug={org.slug} current="calendar" />

        <p className="gk-sm meetings__lead">
          {/* FR-M9: calendars are read-only. FR-M3: the bot joins, never the user's camera or mic. */}
          Upcoming meetings from your calendar. The notetaker joins Meet, Zoom and Teams calls as its own participant,
          never through your camera or microphone. Turn on Auto-record to send it when the meeting starts, or send it
          now. Google Meet can also be recorded with the Chrome extension.
        </p>

        {connectNotice ? (
          <Callout
            tone={connectNotice.tone}
            icon={connectNotice.tone === "success" ? "check-circle" : "warning-circle"}
            role={connectNotice.tone === "success" ? "status" : "alert"}
          >
            {connectNotice.message}
          </Callout>
        ) : null}
        {error ? (
          <Callout tone="danger" icon="warning-circle">
            {error}
          </Callout>
        ) : null}
        {syncErrors.map((p) => (
          <Callout key={p} tone="danger" icon="warning-circle">
            {calendarProviderLabel(p)} couldn&apos;t sync: {status?.[p].last_error}{" "}
            <Link to={CALENDAR_SETTINGS}>Manage calendars</Link>
          </Callout>
        ))}

        {/* With nothing scheduled the left column is one short card, so stack the columns as the
            narrow layout does rather than leave a lone card beside Calendars. */}
        <div className={`meetings__grid${!loading && events.length === 0 ? " meetings__grid--stacked" : ""}`}>
          <div className="meetings__col">
            {activeBots.length > 0 ? (
              <section className="meetings__group" aria-label="Notetaker in a meeting">
                <div className="gk-overline">Notetaker in a meeting</div>
                {activeBots.map((bot) => (
                  <Card key={bot.id} size="sm" className="meetings__bot">
                    {/* FR-M3b / FR-M4: the bot is visibly a bot and the meeting is told — but only
                        claim it is recording once it actually is (a queued bot is not in the meeting). */}
                    {isRecordingBotStatus(bot.ui_status) ? (
                      <Callout tone="record" icon="record" role="status">
                        {bot.consent_banner || `${bot.display_name} is recording this meeting.`}
                      </Callout>
                    ) : (
                      <Callout tone="info" icon="clock" role="status">
                        {botPendingMessage(
                          bot.ui_status,
                          bot.display_name,
                          bot.join_mode === "headless" ? secondsSince(bot.started_at) : 0,
                        )}
                      </Callout>
                    )}
                    <div className="meetings__bot-meta">
                      <Badge tone="accent" icon="robot">
                        {bot.display_name} (bot)
                      </Badge>
                      <Badge tone={botStatusTone(bot.ui_status)} icon={botStatusIcon(bot.ui_status)}>
                        {botStatusLabel(bot.ui_status)}
                      </Badge>
                      <Badge tone={PLATFORM_TONE[bot.platform]}>{platformLabel(bot.platform)}</Badge>
                      <Badge tone="neutral" icon="video-camera-slash">
                        Camera off · mic off
                      </Badge>
                      {bot.grackle_id ? <Link to={`/g/${bot.grackle_id}`}>Open recording</Link> : null}
                    </div>
                    {bot.consent_chat && isRecordingBotStatus(bot.ui_status) ? (
                      <p className="gk-caption meetings__chat">{bot.consent_chat}</p>
                    ) : null}
                    {bot.error ? (
                      <Callout tone="danger" icon="warning-circle">
                        {bot.error}
                      </Callout>
                    ) : null}
                    <div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void onLeave(bot.id)}
                        disabled={busyId === `leave-${bot.id}`}
                      >
                        Remove bot
                      </Button>
                    </div>
                  </Card>
                ))}
              </section>
            ) : null}

            {loading ? (
              <p className="gk-sm">Loading…</p>
            ) : events.length === 0 ? (
              <Card size="md" className="meetings__empty">
                <Icon name="calendar-blank" />
                <p className="gk-sm">No upcoming meetings. Connect a calendar or use the sample calendar.</p>
              </Card>
            ) : (
              groupByDay(events).map((group) => (
                <section key={group.heading} className="meetings__group" aria-label={group.heading}>
                  <div className="gk-overline">{group.heading}</div>
                  {group.events.map((event) => {
                    // Not `activeBots` here (unlike the dedicated section above): once the bot
                    // has finished, its badge is how the "notes link when ready" shows up — a
                    // bot found at all with a `grackle_id` is the recording this event produced.
                    // An active bot in this call comes first, whoever sent it: a colleague's
                    // copy of the same invite has its own event, so match on the link too. Only
                    // active ones by link, though — a recurring meeting reuses its link every
                    // week, and last week's finished bot isn't this meeting's.
                    const key = event.join_url ? meetingKey(event.join_url) : null;
                    const bot =
                      bots.find(
                        (b) =>
                          isActiveBotStatus(b.ui_status) &&
                          (b.event_id === event.id || (key !== null && meetingKey(b.join_url) === key)),
                      ) ?? bots.find((b) => b.event_id === event.id);
                    // One notetaker per meeting: while your bot (or one you asked to share) is on
                    // its way in or in the call, its badge stands in for Send notetaker. A
                    // colleague's keeps the button: sending adds you to theirs (FR-M3e).
                    const botActive = bot ? isActiveBotStatus(bot.ui_status) : false;
                    const botIsMine =
                      !!bot && !!user && (bot.owner_id === user.id || bot.requester_ids.includes(user.id));
                    return (
                      <EventCard
                        key={event.id}
                        start={clock24(event.starts_at) ?? ""}
                        end={clock24(event.ends_at)}
                        title={event.title || "Untitled meeting"}
                        platform={platformLabel(event.platform)}
                        modes={[]}
                        actions={
                          <>
                            {bot ? (
                              <Badge tone={botStatusTone(bot.ui_status)} icon={botStatusIcon(bot.ui_status)} size="sm">
                                {botStatusLabel(bot.ui_status)}
                              </Badge>
                            ) : null}
                            {bot && user && bot.owner_id !== user.id ? (
                              <span className="gk-caption">Sent by {bot.owner_name}</span>
                            ) : null}
                            {/* Auto-record is per meeting, so it sits with the meeting's other
                                actions; only meetings the notetaker can join get it. */}
                            {botSupported(event.platform) ? (
                              <label className="meetings__auto gk-sm">
                                <Switch
                                  size="sm"
                                  label="Auto-record"
                                  checked={event.auto_record}
                                  onChange={(v) => {
                                    if (busyId !== `auto-${event.id}`) void onAuto(event, v);
                                  }}
                                />
                                Auto-record
                              </label>
                            ) : null}
                            {!botSupported(event.platform) ? (
                              <span className="gk-caption">No join URL</span>
                            ) : botActive && botIsMine ? null : (
                              <Button
                                size="sm"
                                icon="robot"
                                onClick={() => void onJoin(event)}
                                disabled={busyId === `join-${event.id}`}
                              >
                                {busyId === `join-${event.id}`
                                  ? "Joining…"
                                  : isLiveNow(event)
                                    ? "Send bot now"
                                    : "Send notetaker"}
                              </Button>
                            )}
                            {/* FR-M3c: Meet offers both — the extension (FR-M2, still recommended)
                                and the bot. Last, matching the lead copy, so the row only loses its tail where
                                this browser can't run the extension. */}
                            {event.platform === "meet" && canUseExtension ? (
                              <Link to={liveNotesPath(org.slug)} className="gk-btn gk-btn--secondary gk-btn--sm">
                                <Icon name="google-chrome-logo" size={18} />
                                Record with extension
                              </Link>
                            ) : null}
                            {event.grackle_id ? <Link to={`/g/${event.grackle_id}`}>Recording attached</Link> : null}
                          </>
                        }
                      />
                    );
                  })}
                </section>
              ))
            )}
          </div>

          <div className="meetings__col">
            {!anyConnected ? (
              <Card size="lg" title="Calendars">
                {status && providers.length === 0 ? (
                  <p className="gk-sm">Calendar connections aren&apos;t available on this server yet.</p>
                ) : (
                  providers.map((p) => (
                    <CalendarProviderBlock
                      key={p.provider}
                      label={p.label}
                      provider={p.provider}
                      orgId={org.id}
                      info={status?.[p.provider]}
                      stub={stub}
                      busy={busyId}
                      onStub={() => void onStub(p.provider)}
                      onDisconnect={() => {}}
                      platformAdmin={platformAdmin}
                    />
                  ))
                )}
              </Card>
            ) : null}

            <Card size="lg" title="Send notetaker by URL">
              <p className="gk-sm">Meet, Zoom or Teams join link.</p>
              <div className="meetings__url">
                <Input
                  icon="link-simple"
                  placeholder="https://meet.google.com/…, Zoom or Teams join URL"
                  aria-label="Meeting join URL"
                  value={joinUrl}
                  onChange={(e) => setJoinUrl(e.target.value)}
                  wrapperStyle={{ flex: 1 }}
                />
                <Input
                  icon="pencil-simple"
                  placeholder="Title (optional)"
                  aria-label="Meeting title"
                  value={joinTitle}
                  maxLength={TITLE_MAX}
                  onChange={(e) => setJoinTitle(e.target.value)}
                  wrapperStyle={{ flex: 1 }}
                />
                <Button onClick={() => void onJoinUrl()} disabled={!joinUrl.trim() || busyId === "join-url"}>
                  {busyId === "join-url" ? "Joining…" : "Send notetaker"}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
